import { useEffect, useMemo } from 'react';

import { mapAnalysisStats } from '@/shared/api/mappers/analysis';
import i18n from '@/shared/i18n';
import { type AnalysisSegment, toSegmentInput } from '@/shared/utils/analysis/segment';
import type { ProductItemCondition, ProductStatus } from '@/shared/api/types/generated';
import { useGetAnalysisDashboardQuery, useGetAvailableItemConditionsQuery } from '@/shared/hooks/queries/generated';

import type { AnalysisStatisticsData } from '../types';

interface UseAnalysisStatisticsParams {
  analysisId: string | undefined;
  /** Segment client (ids) — recalcul serveur quand non vide. */
  segment?: AnalysisSegment;
  onAvailableConditionsLoaded?: (conditions: ProductItemCondition[], defaultCondition: ProductItemCondition) => void;
}

interface UseAnalysisStatisticsReturn {
  data: AnalysisStatisticsData | undefined;
  dailyActivity: { date: string; added: number; sales: number }[] | null;
  weeklyPattern: { day: number; slot: number; added: number; sales: number }[] | null;
  saleTimeDistribution: { maxHours: number; count: number }[] | null;
  characteristics: { conditions: { key: string; soldPct: number; sales: number }[]; sizes: { key: string; soldPct: number; sales: number }[] } | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  availableConditions: ProductItemCondition[];
  availableConditionsLoading: boolean;
  defaultCondition: ProductItemCondition | null;
}

export const useAnalysisStatistics = ({
  analysisId,
  segment,
  onAvailableConditionsLoaded,
}: UseAnalysisStatisticsParams): UseAnalysisStatisticsReturn => {
  // Récupération des conditions disponibles
  const {
    data: availableConditionsData,
    isLoading: availableConditionsLoading,
    error: availableConditionsError,
  } = useGetAvailableItemConditionsQuery(
    {
      analysisId: analysisId ?? '',
      status: 'sold' as ProductStatus,
    },
    {
      enabled: !!analysisId,
    },
  );

  // Extraction des conditions disponibles et détermination de la condition par défaut
  const { availableConditions, defaultCondition } = useMemo(() => {
    if (!availableConditionsData?.getAvailableItemConditions) {
      return { availableConditions: [], defaultCondition: null };
    }

    const conditions = availableConditionsData.getAvailableItemConditions.map(
      (item) => item.itemCondition,
    ) as ProductItemCondition[];

    return { availableConditions: conditions, defaultCondition: null };
  }, [availableConditionsData]);

  // Notification du parent quand les conditions sont chargées
  useEffect(() => {
    if (defaultCondition && onAvailableConditionsLoaded) {
      onAvailableConditionsLoaded(availableConditions, defaultCondition);
    }
  }, [defaultCondition, availableConditions, onAvailableConditionsLoaded]);

  // Récupération des statistiques
  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useGetAnalysisDashboardQuery(
    {
      analysisId: analysisId ?? '',
      segment: segment ? toSegmentInput(segment) : null,
    },
    {
      enabled: !!analysisId,
      // Snapshot figé à la création : jamais périmé
      staleTime: Infinity,
      gcTime: 30 * 60 * 1000,
    },
  );

  const mappedData = data?.getAnalysisDashboard ? mapAnalysisStats(data.getAnalysisDashboard.stats) : undefined;
  const dashboard = data?.getAnalysisDashboard;

  // Séries graphiques réelles (snapshot serveur)
  const dailyActivity = dashboard?.dailyActivity ?? null;
  const weeklyPattern = dashboard?.weeklyHeatmap ?? null;
  const saleTimeDistribution = dashboard?.saleTimeDistribution ?? null;
  // Les caractéristiques serveur ont déjà un titre humain : key = title
  const characteristics = dashboard
    ? {
        conditions: dashboard.characteristics.conditions.map((c) => ({ key: c.title, soldPct: c.soldPct, sales: c.sales })),
        sizes: dashboard.characteristics.sizes.map((s) => ({ key: s.title, soldPct: s.soldPct, sales: s.sales })),
      }
    : null;

  const combinedError = error || availableConditionsError;

  return {
    data: mappedData,
    dailyActivity,
    weeklyPattern,
    saleTimeDistribution,
    characteristics,
    loading,
    error: combinedError ? i18n.t('statsPanel.loadError') : null,
    refetch: () => {
      refetch();
    },
    availableConditions,
    availableConditionsLoading,
    defaultCondition,
  };
};
